import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal, SplitLines, EASE } from "../components/Reveal";
import { ZoomImage } from "../components/Lightbox";
import { Sparkles } from "../components/Sparkles";
import { Contact } from "../components/Contact";
import { BUSINESS } from "../data/business";
import { usePageMeta } from "../lib/usePageMeta";

const VALUES = [
  ["Transparency", "Growth method, treatment and grading disclosed on every invoice. Nothing hidden behind a showroom counter."],
  ["Education", "We explain what matters for each shape, sieve and clarity so you buy with understanding, not pressure."],
  ["Craft", "Lab-grown rough cut for beauty rather than yield — 3EX rounds, well-proportioned fancies, hand-sorted melee."],
];

export default function About() {
  usePageMeta(
    "About Padmavati Gem & Jewelss | Trusted Lab Grown Diamond Dealer in Abids, Hyderabad",
    "Lab grown vs natural diamonds explained by a GST-registered diamond dealer in Hyderabad. CVD and HPHT lab grown diamond jewellery, engagement rings, solitaires and custom designs with transparent grading."
  );
  return (
    <main data-testid="about-page" className="pt-[72px]">
      <section className="relative py-24 lg:py-32 grain">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <p className="eyebrow mb-6">About us</p>
            <h1 className="font-display font-light text-ivory text-[clamp(2.8rem,7vw,6.5rem)] leading-[0.95]">
              <SplitLines lines={["Rooted in", <span key="a" className="text-saffron italic">Abids.</span>]} />
            </h1>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="mt-8 space-y-5 text-ivory/65 font-light leading-relaxed max-w-xl">
              <p>
                {BUSINESS.name} is a lab-grown diamond house in the heart of Hyderabad's historic jewellery quarter. Founded in {BUSINESS.established} by
                {" "}{BUSINESS.proprietor}, we work at the intersection of the trade and the individual buyer.
              </p>
              <p>
                To jewellers and manufacturers we supply calibrated CVD and HPHT melee and pointer parcels. To individuals we offer IGI-certified
                solitaires viewed under a lamp and a loupe, with the patience to explain every grade.
              </p>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 1.3, ease: EASE }} className="lg:col-span-6 grid grid-cols-5 gap-4">
            <div className="col-span-3 relative img-frame">
              <div className="absolute -inset-3 border border-gold/25 pointer-events-none" />
              <div className="relative overflow-hidden">
                <ZoomImage src="/img/loupe.jpg" alt="Diamond grader checking a lab grown diamond under a 10x loupe in Abids, Hyderabad" className="w-full aspect-[4/5] object-cover" />
                <Sparkles count={6} />
              </div>
            </div>
            <div className="col-span-2 self-end relative img-frame overflow-hidden">
              <ZoomImage src="/img/model.jpg" alt="Model wearing lab grown diamond earrings and a lab grown diamond necklace" className="w-full aspect-[3/4] object-cover" />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="border-y border-gold/10 bg-[#0d0d0d] py-24">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal><p className="eyebrow mb-6">What we stand for</p></Reveal>
            <h2 className="font-display font-light text-ivory text-[clamp(2.2rem,4.5vw,4rem)] leading-[0.95]">
              <SplitLines inView lines={["Three", <span key="a" className="text-saffron italic">promises.</span>]} />
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-px" data-testid="about-values">
            {VALUES.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.08} className="grid sm:grid-cols-12 gap-4 py-8 border-t border-gold/15 last:border-b group">
                <p className="sm:col-span-1 font-display text-3xl text-gold/30 group-hover:text-saffron transition-colors">{`0${i + 1}`}</p>
                <h3 className="sm:col-span-4 font-display text-3xl text-ivory">{t}</h3>
                <p className="sm:col-span-7 text-ivory/65 font-light leading-relaxed">{d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
          <Reveal><p className="eyebrow mb-6">Registered & verifiable</p></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-gold/15 border border-gold/15" data-testid="about-credentials">
            {[["GSTIN", BUSINESS.gst], ["Shop registration", BUSINESS.shopReg], ["Proprietor", BUSINESS.proprietor], ["Established", BUSINESS.established]].map(([k, v], i) => (
              <Reveal key={k} delay={i * 0.06} className="bg-night p-8">
                <p className="eyebrow">{k}</p>
                <p className="font-display text-xl sm:text-2xl text-ivory mt-3 break-words">{v}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-10 flex flex-wrap gap-4">
            <Link to="/browse" data-testid="about-browse-btn" className="btn-gold">Browse stones</Link>
            <Link to="/bulk-order" data-testid="about-bulk-btn" className="btn-outline">Bulk orders</Link>
          </Reveal>
        </div>
      </section>

      <Contact />
    </main>
  );
}
